import type { UseQueryResult } from '@tanstack/react-query'
import { useMemo } from 'react'

import type { ActivationGroup, ApiActivationGroupResource } from '@/features/activationGroup/types'
import { applyCapacityReductionAndRoundDown } from '@/utils/applyCapacityReductionAndRoundDown'

export type RegulationObjectGroup = {
  regulationObjectCode: string
  activationGroups: ActivationGroup[]
  totalCapacity: number
}

type UseBidOfferCascadingDerivedDataParams = {
  eligibleActivationGroups: ActivationGroup[]
  resourcesQueryResults: UseQueryResult<ApiActivationGroupResource[], Error>[]
}

export type UseBidOfferCascadingDerivedDataReturn = {
  capacityByActivationGroup: Map<string, number>
  regulationObjectGroups: RegulationObjectGroup[]
}

export function useBidOfferCascadingDerivedData({
  eligibleActivationGroups,
  resourcesQueryResults,
}: UseBidOfferCascadingDerivedDataParams): UseBidOfferCascadingDerivedDataReturn {
  // Calculate total capacity for each activation group by summing qualifiedCapacity
  // of all LIVE resources within that group. Non-activatable resources are excluded:
  // they are left out of the group's BID activation schedules, so they cannot be activated
  // and must not count towards the offerable capacity.
  const capacityByActivationGroup = useMemo(() => {
    const map = new Map<string, number>()

    resourcesQueryResults.forEach((result, index) => {
      const activationGroupUuid = eligibleActivationGroups[index]?.uuid
      if (activationGroupUuid && result.data) {
        const totalCapacity = result.data
          .filter((resource) => resource.lifecycleStage === 'LIVE' && resource.isActivatable)
          .reduce((sum, resource) => sum + (resource.qualifiedCapacity ?? 0), 0)

        map.set(activationGroupUuid, applyCapacityReductionAndRoundDown(totalCapacity))
      }
    })

    return map
  }, [resourcesQueryResults, eligibleActivationGroups])

  // Group activation groups by regulationObjectCode and calculate total capacity per group
  const regulationObjectGroups = useMemo(() => {
    const groupMap = new Map<string, ActivationGroup[]>()

    eligibleActivationGroups.forEach((ag) => {
      const regCode = ag.regulationObjectCode ?? 'unknown'
      const existing = groupMap.get(regCode) ?? []
      groupMap.set(regCode, [...existing, ag])
    })

    const groups: RegulationObjectGroup[] = []

    groupMap.forEach((activationGroups, regulationObjectCode) => {
      const totalCapacity = activationGroups.reduce((sum, ag) => {
        return sum + (capacityByActivationGroup.get(ag.uuid) ?? 0)
      }, 0)

      groups.push({
        regulationObjectCode,
        activationGroups,
        totalCapacity,
      })
    })

    return groups
  }, [eligibleActivationGroups, capacityByActivationGroup])

  return useMemo(
    () => ({
      capacityByActivationGroup,
      regulationObjectGroups,
    }),
    [capacityByActivationGroup, regulationObjectGroups],
  )
}
